import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Collapsible from '../Collapsible';
import { FilterGroup } from './types/FilterGroup';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { FilterGroupType } from './types/FilterGroupType';
import { FilterGroupOption } from './types/FilterGroupOption';
import FilterGroupElement from './FilterGroupElement';
import { useDispatch } from 'react-redux';
import {
  queryProductFiltering,
  toggleUpdatePending,
  toggleShowAllForFilter,
  setFilterOpen,
} from '../../Actions/ProductFiltering.action';

const MAX_FILTERS_BEFORE_SHOW_MORE = 9;

const ProductFiltering = () => {
  const productFiltering = useTypedSelector(
    (state) => state.productFiltering
  );
  const { filters, updatePending, showAllForFilters, openFilters } =
    productFiltering;
  const dispatch = useDispatch();
  const showFilterState = useTypedSelector(
    (state) => state.productFiltering.showDesktopFilters
  );

  const toggleSideBar = () => {
    const sideBarElem = document.getElementById('category-filter-aside');
    if (sideBarElem) {
      if (showFilterState) {
        sideBarElem.classList.add('filter-visible');
      } else {
        sideBarElem.classList.remove('filter-visible');
      }
    }
  };

  useEffect(() => toggleSideBar(), [showFilterState]);
  const [filterSearchResults, setFilterSearchResults] = useState<{
    [filterId: string]: FilterGroupOption[];
  }>({});

  useEffect(() => {
    if (filters?.length! > 0 || updatePending) return;
    dispatch(queryProductFiltering(null, true));
  }, []);

  // Returns true if the filter has "show all" selected
  const shouldShowAllFilters = useCallback(
    (filterId: string) =>
      showAllForFilters && showAllForFilters.indexOf(filterId) != -1,
    [showAllForFilters]
  );

  // Called when the search input was changed for the specified filter
  const onSearch = useCallback(
    (filter: FilterGroup, options: FilterGroupOption[]) => {
      setFilterSearchResults((current) => {
        const filterSearchResultsCopy = { ...current };
        if (!options) {
          if (filterSearchResultsCopy.hasOwnProperty(filter.id)) {
            delete filterSearchResultsCopy[filter.id];
          }
        } else {
          filterSearchResultsCopy[filter.id] = options;
        }
        return filterSearchResultsCopy ?? {};
      });
    },
    []
  );

  const isOpen = useCallback(
    (filterId: string) =>
      openFilters && openFilters.indexOf(filterId) != -1,
    [openFilters]
  );

  // Get options from the specified filter, with regard to the search value for the filter
  const getFilterOptions = useCallback(
    (filter: FilterGroup) =>
      filterSearchResults.hasOwnProperty(filter.id) &&
      filterSearchResults[filter.id] != undefined
        ? filterSearchResults[filter.id] ?? filter.options
        : filter.options,
    [filterSearchResults]
  );

  const getFilterGroupLabel = useCallback(
    (filter: FilterGroup) =>
      `${
        filter.filterGroupType != FilterGroupType.Range &&
        filter.selectedOptions.length > 0
          ? `(${filter.selectedOptions.length})`
          : ''
      } ${filter.label}`.trim(),
    []
  );

  return (
    <div className="filter-container [&_>.collapsible:last-of-type]:lg:border-b [&_>.collapsible]:!border-b-0 [&_>.collapsible]:border-gray-100 [&_>.collapsible]:lg:border-t [&_>.collapsible]:md-down:!border-b">
      {filters &&
        filters.map((filter) => (
          <Collapsible
            className="border-b border-solid !border-gray-100 px-4 first:border-t-0 lg:px-0"
            key={filter.id}
            label={getFilterGroupLabel(filter)}
            labelClassName="!text-h5 h5 [&_>svg]:!w-5 [&_>svg]:!h-5 store-screen:py-3"
            contentClassName="[&_.ReactCollapse--content]:pb-4"
            isOpen={
              (openFilters && openFilters.indexOf(filter.id) != -1) ||
              filter.selectedOptions.length > 0
            }
            onStateChange={(open) =>
              dispatch(setFilterOpen(filter.id, open))
            }
          >
            <FilterGroupElement
              filter={filter}
              options={getFilterOptions(filter).slice(
                0,
                shouldShowAllFilters(filter.id)
                  ? undefined
                  : MAX_FILTERS_BEFORE_SHOW_MORE
              )}
              showAll={shouldShowAllFilters(filter.id)}
              onSearch={onSearch}
              onShowHideAllClick={() =>
                dispatch(toggleShowAllForFilter(filter.id))
              }
              maxFiltersToShow={MAX_FILTERS_BEFORE_SHOW_MORE}
              showAllButtonVisible={
                getFilterOptions(filter).length >
                MAX_FILTERS_BEFORE_SHOW_MORE
              }
            />
          </Collapsible>
        ))}
    </div>
  );
};

export default ProductFiltering;
